import * as Select from '@radix-ui/react-select';
import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { chevron } from '~/chords/DropdownSingleSelect/styles';
import type {
  DropdownState,
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/chords/DropdownSingleSelect/types';
import { Icon } from '~/chords/Icon';
import { cn } from '~/lib/cn';

interface DropdownTriggerProps extends RadixUiProps {
  id?: string;
  placeholder?: string;
  selectedOption?: InternalDropdownOption;
  isOpen: boolean;
  state?: DropdownState;
  disabled?: boolean;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownTrigger({
  id,
  placeholder,
  selectedOption,
  isOpen,
  state,
  disabled,
  OptionTemplate,
  radix = {},
}: DropdownTriggerProps) {
  const { t } = useTranslation('common');

  const placeholderText = placeholder || t('dropdownTree.placeholder');
  let valueDisplay: JSX.Element | ReactNode = placeholderText;

  if (selectedOption) {
    if (OptionTemplate) {
      valueDisplay = (
        <OptionTemplate option={selectedOption} isTrigger={true} />
      );
    } else {
      valueDisplay = (
        <span className="truncate">
          {selectedOption.label || selectedOption.value}
        </span>
      );
    }
  }

  return (
    <Select.Trigger
      {...radix.selectTrigger}
      id={id}
      className={cn(
        'relative flex min-h-2xl w-full cursor-pointer items-stretch justify-between gap-sm rounded-xs border pr-sm text-body-sm',
        'outline-none transition-colors duration-300 bg-emphasis-default border-emphasis-subtle',
        'enabled:hover:bg-emphasis-subtle enabled:hover:border-emphasis-strong disabled:cursor-not-allowed disabled:border-none',
        {
          'pl-md': !(OptionTemplate && selectedOption),
          'border-error-default': state === 'error',
          'border-success-default': state === 'success',
          'border-warning-default': state === 'warning',
        },
        radix.selectTrigger?.className,
      )}
      aria-invalid={state === 'error'}
    >
      <Select.Value
        {...radix.selectValue}
        placeholder={
          <div className="flex items-center text-subtle">{placeholderText}</div>
        }
        asChild
      >
        <div
          className={cn(
            {
              'text-disabled': disabled,
            },
            'flex items-center truncate',
            radix.selectValue?.className,
          )}
        >
          {valueDisplay}
        </div>
      </Select.Value>
      <Select.Icon
        {...radix.selectIcon}
        className={cn(
          'flex items-center text-default',
          radix.selectIcon?.className,
          {
            'text-disabled': disabled,
          },
        )}
      >
        <div
          className={cn(
            chevron({
              state: isOpen ? 'open' : 'default',
            }),
            'mr-sm',
          )}
        >
          <Icon size="md" type="chevron-down" />
        </div>
      </Select.Icon>
    </Select.Trigger>
  );
}

DropdownTrigger.displayName = 'DropdownTrigger';
